import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useWebSocket } from '../hooks/useWebSocket';

const WS_URL = 'wss://mimi-v2-api.dawn-flower-35e5.workers.dev/api/ws';
const API_URL = 'https://mimi-v2-api.dawn-flower-35e5.workers.dev/api';

interface Message { id: string; conversationId: string; senderId: string; type: string; content: string; timestamp: number; }

export function ChatPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [conversationId, setConversationId] = useState<string>(id === 'new' ? '' : id || '');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!token) return;
    if (id === 'new' && !conversationId) {
      createConversation();
      return;
    }
    connectWS();
  }, [id]);

  async function createConversation() {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ participants: [] }),
      });
      const data = await res.json();
      if (data.success) {
        setConversationId(data.data.id);
        navigate(`/app/chat/${data.data.id}`, { replace: true });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function connectWS() {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(WS_URL + (token ? `?token=${token}` : ''));
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'auth_ok') {}
        if (msg.type === 'new_message' || msg.type === 'message_sent') {
          const m = msg.message;
          if (m.conversationId === conversationId) {
            setMessages(prev => [...prev.filter(x => x.id !== m.id), m]);
          }
        }
        if (msg.type === 'typing') setTyping(true);
      } catch {}
    };
    ws.onclose = () => { setConnected(false); setTimeout(connectWS, 3000); };
    ws.onerror = () => ws.close();
  }

  const sendMessage = () => {
    if (!input.trim() || !conversationId || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'send_message', conversationId, contentType: 'text', content: input }));
    setInput('');
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/app')} className="p-2 hover:bg-gray-100 rounded-lg">←</button>
        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-lg">🐱</div>
        <div>
          <p className="font-semibold">MIMI Chat</p>
          <p className="text-xs text-gray-500">{connected ? '🟢 ออนไลน์' : '🔴 เชื่อมต่อ...'}</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {loading && <div className="text-center text-gray-400 mt-20"><p>กำลังสร้างการสนทนา...</p></div>}
        {messages.length === 0 && !loading && (
          <div className="text-center text-gray-400 mt-20">
            <p className="text-5xl mb-3">🐱</p>
            <p>เริ่มสนทนากับ MIMI ได้เลย!</p>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xs px-4 py-2 rounded-2xl ${msg.senderId === user?.id ? 'bg-purple-600 text-white' : 'bg-white border'}`}>
              <p>{msg.content}</p>
              <p className={`text-xs mt-1 ${msg.senderId === user?.id ? 'text-purple-200' : 'text-gray-400'}`}>
                {new Date(msg.timestamp).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        {typing && <p className="text-sm text-gray-400">MIMI กำลังพิมพ์... 💭</p>}
        <div ref={useRef(null)} />
      </div>
      <div className="bg-white border-t p-4 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()}
          placeholder="พิมพ์ข้อความ..." className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500" />
        <button onClick={sendMessage} disabled={!connected || !conversationId}
          className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 disabled:opacity-50">→</button>
      </div>
    </div>
  );
}
